<script lang="ts">
	import { getDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	import { devices_settings } from 'src/stores/device-runtime/status';
	//Services
	import { format } from '../../../../services/i18n';
	import { gen2Request } from '../../../../services/gen_2_http_reqs';
	//Helpers
	import {
		get_gen_2_component_from_category,
		real_id,
		get_device_component_index,
	} from '../../../../helpers/interface_helpers';
	//Stores
	//Types
	import type { SettingRequest } from '../../../../types/requests/settings/setting';
	//Design system components
	import { Button, ButtonV2, Input, InputV2, toast } from '@Shelly/shelly-design-system';
	import {
		DEVICES_WITH_MAX_VOLTAGE_PROTECTION,
		LIGHTS_WITH_HIGH_VOLTAGE_PROTECTION,
	} from 'src/constants';
	import { restrictDecimalNumberInput } from 'src/helpers/number_input_helpers';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import { bindStickyBottomAction } from 'src/helpers/sticky_bottom_action';
	import SettingGuide from '../../SettingGuide.svelte';
	import ResetToDefaultLink from '../../ResetToDefaultLink.svelte';

	$: settingsStore = getDeviceSettings(id);
	export let device;

	let id = real_id(device.id);
	const deviceType = get_gen_2_component_from_category(device);
	const index = get_device_component_index(device);
	const componentKey = `${deviceType}:${index}`;
	$: settings = $settingsStore || {};
	$: componentSettings = $settingsStore?.[componentKey];

	const unit = 'V';
	// const prop = $format('prop_vol_meter', { default: 'the voltage' });

	let value;
	$: if (value === undefined && componentSettings) {
		value = componentSettings.voltage_limit;
	}
	// let cacheValue = value;
	let loading = false;
	$: error =
		+value < min || +value > max
			? $format('error_out_of_range', {
					default: `Value ${value} ${unit} is out of range. Please provide a value within the limits. (${min} ${unit} — ${max} ${unit})`,
					values: { value, max, min, unit },
				})
			: '';

	/**
	 * Determines the maximum voltage protection value based on device type and category
	 * @returns {number} The maximum voltage value:
	 * - 30V for light devices (except those with high voltage protection)
	 * - Device-specific value from DEVICES_WITH_MAX_VOLTAGE_PROTECTION if defined
	 * - 280V as default fallback
	 */
	const handleMaxValue = () => {
		return device?.category === 'light' && !LIGHTS_WITH_HIGH_VOLTAGE_PROTECTION.includes(device.type)
			? 30
			: typeof DEVICES_WITH_MAX_VOLTAGE_PROTECTION[device.type] !== 'undefined'
				? DEVICES_WITH_MAX_VOLTAGE_PROTECTION[device.type]
				: 280;
	};

	// $: info = $format('info_value_range', {
	// 	default: `Configure Shelly device to turn off when ${prop} reaches ${value} ${unit}. Max: ${max} ${unit}.`,
	// 	values: { prop, value, max, unit }
	// });

	// ToDo: adapt the infor key ("max_voltage_prot_description") in the same way.

	const min = 0;
	const max = handleMaxValue();

	const setVoltageProtection = async () => {
		if (!componentSettings) return;

		if (value === '') {
			value = max;
		}
		// if (cacheValue !== value) {
		if (+value < min || +value > max) {
			return toast.open({
				type: 'danger',
				title: $format('max_voltage_prot_limit_err_toast'),
			});
		}

		const params = {
			id: index,
			config: {
				power_limit: componentSettings.power_limit,
				voltage_limit: +value,
				current_limit: componentSettings.current_limit,
			},
		};
		loading = true;

		const res: SettingRequest = await gen2Request(device, `${deviceType}.setconfig`, params);

		if (res.isok) {
			settings[componentKey].voltage_limit = value;
			devices_settings.update_devices_settings(id, settings);

			toast.open({
				type: 'success',
				title: $format('max_voltage_prot_save_scc_toast', {
					values: { value: value },
				}),
			});
			// cacheValue = value;
		} else {
			// value = cacheValue;
			toast.open({
				type: 'danger',
				title: $format('max_voltage_prot_save_err_toast'),
			});
		}
		loading = false;
		// }
	};

	const { stickyBottomAction, setStickyBottomAction } = bindStickyBottomAction();

	$: if (stickyBottomAction) {
		if ($showNewLayout) {
			setStickyBottomAction({
				onClick: setVoltageProtection,
				loading,
				disabled: loading || !componentSettings,
				icon: 'fas fa-save',
			});
		} else {
			setStickyBottomAction(null);
		}
	}

	$: InputC = $showNewLayout ? InputV2 : Input;
	$: ButtonC = $showNewLayout ? ButtonV2 : Button;

	const resetToDefault = () => {
		value = max;
	};
</script>

<SettingGuide
	text={$format('max_voltage_prot_description', {
		values: { value },
		default: `Configure Shelly device to turn off when voltage reaches ${value} V.`,
	})}
/>

<svelte:component
	this={InputC}
	on:input={(e) => restrictDecimalNumberInput(e, 2)}
	size="m"
	bind:value
	type="number"
	{min}
	{max}
	info={$format('range_min_max_volt_value', {
		default: 'Range: {min} V - {max} V.',
		values: { min, max },
	})}
	{error}
	step={1}
	on:enter={setVoltageProtection}
	aria-label={$format('max_voltage_prot_description', {
		default: 'Max voltage protection',
	})}
	id="input-5mlg0bk"
/>
<ResetToDefaultLink disabled={+value === +max} on:click={resetToDefault} />
{#if !$showNewLayout}
	<svelte:component
		this={ButtonC}
		class="mt-s"
		{loading}
		disabled={loading}
		size="m"
		width="full"
		on:click={setVoltageProtection}
	>
		{$format('button_save', { default: 'Save' })}
	</svelte:component>
{/if}
